import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { api } from "../../services/api";
import {
    FaEnvelope, FaPhoneAlt, FaBuilding, FaMapMarkerAlt,
    FaFileInvoiceDollar, FaWhatsapp,
    FaHistory, FaChevronRight, FaTimes, FaRedo
} from "react-icons/fa";
import { buildWhatsAppUrl } from "../../utils/whatsapp";

const getStatusBadge = (status) => {
    switch (status) {
        case "Enquiry Received":
            return "bg-blue-50 text-blue-700 border-blue-200";
        case "Under Review":
            return "bg-amber-50 text-amber-700 border-amber-200";
        case "More Information Required":
            return "bg-purple-50 text-purple-700 border-purple-200";
        case "Quotation Sent":
            return "bg-cyan-50 text-cyan-700 border-cyan-200";
        case "Accepted by Customer":
        case "Order Confirmed":
            return "bg-emerald-50 text-emerald-700 border-emerald-200";
        case "Processing":
            return "bg-indigo-50 text-indigo-700 border-indigo-200";
        case "Dispatched":
            return "bg-teal-50 text-teal-700 border-teal-200";
        case "Delivered":
        case "Completed":
            return "bg-green-50 text-green-700 border-green-200";
        case "Rejected":
        case "Cancelled":
            return "bg-rose-50 text-rose-700 border-rose-200";
        default:
            return "bg-gray-100 text-gray-700 border-gray-200";
    }
};

const UserDashboard = () => {
    const { user } = useAuth();
    const [enquiries, setEnquiries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedEnquiry, setSelectedEnquiry] = useState(null);

    useEffect(() => {
        fetchMyEnquiries();
    }, []);

    const fetchMyEnquiries = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/api/enquiries/my-enquiries");
            setEnquiries(data.data || data || []);
        } catch (error) {
            console.error("Failed to load user enquiries:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleWhatsAppHelp = (item) => {
        const ref = item.enquiryRef || item.referenceNumber || item._id;
        const msg = `Hello LUMIGO team,\n\nI am checking on my project enquiry:\nReference: ${ref}\nProduct: ${item.productName}\nModel: ${item.modelNumber || "N/A"}\nCurrent Status: ${item.status}\n\nPlease share the latest status update.`;
        window.open(buildWhatsAppUrl(msg), "_blank");
    };

    return (
        <div className="space-y-8 animate-fadeIn text-[#263746]">
            {/* User Profile Card */}
            <div className="bg-[#FFFFFF] border border-[#E5E7EB] rounded-2xl p-6 sm:p-8 shadow-sm relative overflow-hidden">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                    <div className="flex items-center gap-4">
                        <div className="w-16 h-16 rounded-2xl bg-[#E3B629]/15 text-[#263746] border border-[#E3B629]/40 flex items-center justify-center text-2xl font-black font-mono">
                            {user?.name?.[0]?.toUpperCase() || "U"}
                        </div>
                        <div>
                            <span className="text-[#E3B629] text-[10px] font-black tracking-[0.25em] uppercase block">
                                Commercial Client Portal
                            </span>
                            <h1 className="text-2xl font-extrabold text-[#263746]">
                                Welcome, {user?.name || "Client"}
                            </h1>
                            <div className="flex flex-wrap gap-4 mt-2 text-xs text-[#687482]">
                                <span className="flex items-center gap-1.5"><FaEnvelope className="text-[#E3B629]" /> {user?.email}</span>
                                {user?.phone && <span className="flex items-center gap-1.5"><FaPhoneAlt className="text-[#E3B629]" /> {user?.phone}</span>}
                                {user?.companyName && <span className="flex items-center gap-1.5"><FaBuilding className="text-[#E3B629]" /> {user?.companyName}</span>}
                                {user?.city && <span className="flex items-center gap-1.5"><FaMapMarkerAlt className="text-[#E3B629]" /> {user?.city}</span>}
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center gap-3">
                        <button
                            onClick={fetchMyEnquiries}
                            className="p-2.5 rounded-lg bg-[#F7F5EF] hover:bg-[#E5E7EB] text-[#263746] border border-[#E5E7EB] text-xs font-bold flex items-center gap-2 transition"
                            title="Refresh Enquiries"
                        >
                            <FaRedo className={loading ? "animate-spin" : ""} /> Refresh
                        </button>
                        <a
                            href={buildWhatsAppUrl(`Hello Fourlifters / LUMIGO team,\n\nI am logged in as ${user?.name} (${user?.email}). I need technical assistance with my projection project.`)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="bg-[#168B53] hover:bg-[#127244] text-white font-bold px-4 py-2.5 rounded-lg text-xs flex items-center gap-2 transition shadow-sm"
                        >
                            <FaWhatsapp /> Help Desk
                        </a>
                    </div>
                </div>
            </div>

            {/* Enquiries & Orders Section */}
            <div className="space-y-4">
                <div className="flex items-center justify-between">
                    <div>
                        <h2 className="text-xl font-extrabold text-[#263746] flex items-center gap-2">
                            <FaFileInvoiceDollar className="text-[#E3B629]" /> My Project Enquiries & Orders
                        </h2>
                        <p className="text-xs text-[#687482] mt-0.5">
                            Track quotations, technical review status, and dispatch progress.
                        </p>
                    </div>
                    <div className="text-xs text-[#687482]">
                        Total: <strong className="text-[#263746]">{enquiries.length}</strong>
                    </div>
                </div>

                {loading ? (
                    <div className="py-20 text-center text-xs text-[#687482] font-semibold">
                        Loading your commercial enquiries...
                    </div>
                ) : enquiries.length > 0 ? (
                    <div className="grid grid-cols-1 gap-4">
                        {enquiries.map(item => (
                            <div
                                key={item._id}
                                className="bg-[#FFFFFF] border border-[#E5E7EB] hover:border-[#E3B629] rounded-xl p-5 transition shadow-sm space-y-4"
                            >
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 border-b border-[#E5E7EB]">
                                    <div className="flex items-center gap-3">
                                        <span className="font-mono text-sm font-bold text-[#263746] bg-[#F7F5EF] px-2.5 py-1 rounded border border-[#E5E7EB]">
                                            {item.enquiryRef || item.referenceNumber || item._id.slice(-6)}
                                        </span>
                                        <span className="text-xs text-[#687482]">
                                            {new Date(item.createdAt).toLocaleDateString()}
                                        </span>
                                    </div>

                                    <div className="flex items-center gap-3">
                                        <span className={`px-2.5 py-1 rounded text-xs font-bold border ${getStatusBadge(item.status)}`}>
                                            {item.status}
                                        </span>
                                        <button
                                            onClick={() => setSelectedEnquiry(item)}
                                            className="text-xs text-[#263746] bg-[#F7F5EF] hover:bg-[#E3B629] font-bold px-3 py-1 rounded transition flex items-center gap-1 border border-[#E5E7EB]"
                                        >
                                            View Details <FaChevronRight className="text-[10px]" />
                                        </button>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-xs">
                                    <div>
                                        <span className="text-[10px] text-[#687482] uppercase font-bold block mb-0.5">Projector Model</span>
                                        <div className="font-bold text-[#263746] text-sm">{item.productName}</div>
                                        <div className="text-[#687482] text-xs">Model: {item.modelNumber || "Gobo Projector"} • Qty: {item.quantity || 1}</div>
                                    </div>

                                    <div>
                                        <span className="text-[10px] text-[#687482] uppercase font-bold block mb-0.5">Commercial Quotation</span>
                                        {item.quotedPrice ? (
                                            <div className="text-base font-extrabold text-[#168B53] font-mono">
                                                ₹{item.quotedPrice.toLocaleString()}
                                            </div>
                                        ) : (
                                            <div className="text-xs text-amber-600 font-bold">Under Technical Review</div>
                                        )}
                                        <div className="text-[10px] text-[#687482]">Excludes GST & courier charges</div>
                                    </div>

                                    <div>
                                        <span className="text-[10px] text-[#687482] uppercase font-bold block mb-0.5">Business Update Note</span>
                                        <div className="text-xs text-[#687482] italic">
                                            {item.customerNotes || "Enquiry received. Our engineering desk is calculating throw distance and optics."}
                                        </div>
                                    </div>
                                </div>

                                <div className="flex justify-end gap-3 pt-2">
                                    <button
                                        onClick={() => handleWhatsAppHelp(item)}
                                        className="text-xs text-[#168B53] hover:text-[#127244] font-bold flex items-center gap-1.5 transition"
                                    >
                                        <FaWhatsapp /> Chat on WhatsApp about this Ref
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-20 bg-[#FFFFFF] border border-dashed border-[#E5E7EB] rounded-2xl p-8">
                        <FaFileInvoiceDollar className="text-4xl text-[#687482] mx-auto mb-3" />
                        <h3 className="text-base font-bold text-[#263746] mb-1">No Active Enquiries Found</h3>
                        <p className="text-xs text-[#687482] max-w-sm mx-auto mb-6">
                            You have not submitted any project enquiries yet. Browse our commercial catalogue to request a quote.
                        </p>
                        <a
                            href="/products"
                            className="bg-[#E3B629] hover:bg-[#cca021] text-[#263746] font-extrabold px-6 py-2.5 rounded-lg text-xs uppercase tracking-wider inline-block transition"
                        >
                            Browse Equipment Catalogue
                        </a>
                    </div>
                )}
            </div>

            {/* DETAIL MODAL */}
            {selectedEnquiry && (
                <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
                    <div className="bg-[#FFFFFF] border border-[#E5E7EB] rounded-2xl max-w-2xl w-full p-6 space-y-4 shadow-2xl">
                        <div className="flex justify-between items-start pb-3 border-b border-[#E5E7EB]">
                            <div>
                                <span className="text-[10px] text-[#E3B629] uppercase font-black tracking-widest block">
                                    Enquiry Reference
                                </span>
                                <h3 className="text-xl font-bold font-mono text-[#263746]">
                                    {selectedEnquiry.enquiryRef || selectedEnquiry.referenceNumber || selectedEnquiry._id}
                                </h3>
                                <div className="text-xs text-[#687482]">
                                    Submitted on {new Date(selectedEnquiry.createdAt).toLocaleString()}
                                </div>
                            </div>
                            <button
                                onClick={() => setSelectedEnquiry(null)}
                                className="text-[#687482] hover:text-[#263746] text-lg p-1 transition"
                            >
                                <FaTimes />
                            </button>
                        </div>

                        <div className="space-y-3 text-xs">
                            <div className="p-3.5 rounded-xl bg-[#F7F5EF] border border-[#E5E7EB]">
                                <span className="text-[10px] text-[#687482] uppercase font-bold block mb-1">Hardware Specification</span>
                                <div className="font-bold text-[#263746] text-sm">{selectedEnquiry.productName}</div>
                                <div className="text-[#687482]">Model: {selectedEnquiry.modelNumber || "Gobo Projector"} • Qty: {selectedEnquiry.quantity || 1}</div>
                                {(selectedEnquiry.requirements || selectedEnquiry.customerRequirements) && (
                                    <div className="mt-2 text-[#263746]">
                                        <strong>Your Notes:</strong> {selectedEnquiry.requirements || selectedEnquiry.customerRequirements}
                                    </div>
                                )}
                            </div>

                            <div className="p-3.5 rounded-xl bg-[#F7F5EF] border border-[#E5E7EB] flex items-center justify-between">
                                <div>
                                    <span className="text-[10px] text-[#687482] uppercase font-bold block">Current Status</span>
                                    <span className={`inline-block mt-1 px-2.5 py-0.5 rounded text-xs font-bold border ${getStatusBadge(selectedEnquiry.status)}`}>
                                        {selectedEnquiry.status}
                                    </span>
                                </div>
                                <div className="text-right">
                                    <span className="text-[10px] text-[#687482] uppercase font-bold block">Quoted Price</span>
                                    <span className="text-base font-extrabold text-[#168B53] font-mono">
                                        {selectedEnquiry.quotedPrice ? `₹${selectedEnquiry.quotedPrice.toLocaleString()}` : "Pending Quote"}
                                    </span>
                                </div>
                            </div>

                            {selectedEnquiry.customerNotes && (
                                <div className="p-3.5 rounded-xl bg-[#E3B629]/10 border border-[#E3B629]/30 text-[#263746]">
                                    <span className="font-bold block mb-0.5 text-[#263746]">Admin Advisory Note:</span>
                                    <p className="text-[#687482]">{selectedEnquiry.customerNotes}</p>
                                </div>
                            )}

                            {/* Status Timeline */}
                            {selectedEnquiry.statusHistory && selectedEnquiry.statusHistory.length > 0 && (
                                <div className="border border-[#E5E7EB] rounded-xl p-3 bg-[#F7F5EF]">
                                    <div className="text-[10px] text-[#687482] font-bold uppercase tracking-wider mb-2 flex items-center gap-1.5">
                                        <FaHistory className="text-[#E3B629]" /> Status Timeline
                                    </div>
                                    <div className="space-y-2 max-h-36 overflow-y-auto">
                                        {selectedEnquiry.statusHistory.map((h, idx) => (
                                            <div key={idx} className="flex justify-between items-start text-[11px] border-b border-[#E5E7EB] pb-1">
                                                <div>
                                                    <span className="font-bold text-[#263746]">{h.status}</span>
                                                    {h.note && <p className="text-[#687482] italic">"{h.note}"</p>}
                                                </div>
                                                <span className="font-mono text-[10px] text-[#687482]">
                                                    {new Date(h.changedAt).toLocaleString()}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="pt-3 border-t border-[#E5E7EB] flex justify-between items-center">
                            <button
                                onClick={() => handleWhatsAppHelp(selectedEnquiry)}
                                className="bg-[#168B53] hover:bg-[#127244] text-white font-bold px-4 py-2 rounded-lg text-xs flex items-center gap-1.5 transition"
                            >
                                <FaWhatsapp /> Chat on WhatsApp
                            </button>
                            <button
                                onClick={() => setSelectedEnquiry(null)}
                                className="bg-[#F7F5EF] hover:bg-[#E5E7EB] text-[#263746] px-4 py-2 rounded-lg text-xs font-bold border border-[#E5E7EB] transition"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserDashboard;
